/**
 * 📝 LOGGER PROFESIONAL - Frontend Logger con niveles y formateo
 *
 * Sistema de logging profesional que reemplaza console.log en toda la aplicación.
 *
 * Características:
 * - Niveles de log: debug, info, warn, error
 * - Formato consistente con timestamps
 * - Colores por nivel para facilitar debugging
 * - Desactivable en producción
 * - Soporte para contexto y metadata
 * - Compatible con herramientas de monitoreo (Sentry, LogRocket)
 *
 * @example
 * import { logger } from '@/shared/utils/logger';
 *
 * logger.debug('Usuario cargado', { userId: 123 });
 * logger.info('Formulario enviado correctamente');
 * logger.warn('Caché desactualizado', { cacheAge: 3600 });
 * logger.error('Error al guardar', new Error('Network failed'));
 */

// Niveles de log permitidos
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Configuración del logger
interface LoggerConfig {
	enabled: boolean;
	level: LogLevel;
	includeTimestamp: boolean;
	includeStackTrace: boolean;
}

// Emojis para cada nivel
const EMOJIS = {
	debug: '🔍',
	info: '✅',
	warn: '⚠️',
	error: '❌'
} as const;

// Orden de prioridad de los niveles
const LOG_LEVELS: Record<LogLevel, number> = {
	debug: 0,
	info: 1,
	warn: 2,
	error: 3
};

// Configuración por defecto
const DEFAULT_CONFIG: LoggerConfig = {
	enabled: process.env.NODE_ENV !== 'production',
	level: (process.env.REACT_APP_LOG_LEVEL as LogLevel) || 'debug',
	includeTimestamp: true,
	includeStackTrace: false
};

class Logger {
	private config: LoggerConfig;

	constructor(config: Partial<LoggerConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config };
	}

	/**
	 * Actualiza la configuración del logger
	 */
	configure(config: Partial<LoggerConfig>): void {
		this.config = { ...this.config, ...config };
	}

	/**
	 * Verifica si un nivel de log debe ser mostrado
	 */
	private shouldLog(level: LogLevel): boolean {
		if (!this.config.enabled) return false;
		return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
	}

	/**
	 * Formatea el timestamp
	 */
	private getTimestamp(): string {
		if (!this.config.includeTimestamp) return '';
		const now = new Date();
		return now.toISOString().replace('T', ' ').split('.')[0];
	}

	/**
	 * Formatea el mensaje de log
	 */
	private formatMessage(level: LogLevel, message: string): string {
		const timestamp = this.getTimestamp();
		const emoji = EMOJIS[level];
		const prefix = timestamp ? `[${timestamp}]` : '';

		return `${emoji} ${prefix} [${level.toUpperCase()}] ${message}`;
	}

	/**
	 * Serializa metadata para logging
	 */
	private serializeMetadata(metadata?: any): string | null {
		if (!metadata) return null;

		try {
			// Si es un Error, extraer información útil
			if (metadata instanceof Error) {
				return JSON.stringify({
					name: metadata.name,
					message: metadata.message,
					stack: this.config.includeStackTrace ? metadata.stack : undefined
				}, null, 2);
			}

			// Si es un objeto, stringify con formato
			if (typeof metadata === 'object') {
				return JSON.stringify(metadata, null, 2);
			}

			// Si es primitivo, convertir a string
			return String(metadata);
		} catch {
			// JSON serialization failed - return placeholder
			return '[Unable to serialize metadata]';
		}
	}

	/**
	 * Método interno para realizar el log
	 */
	private log(level: LogLevel, message: string, metadata?: any): void {
		if (!this.shouldLog(level)) return;

		const formattedMessage = this.formatMessage(level, message);
		const serializedMetadata = this.serializeMetadata(metadata);

		// Usar el método de console apropiado
		const consoleMethod = console[level] || console.log;

		if (serializedMetadata) {
			consoleMethod(formattedMessage, '\n', serializedMetadata);
		} else {
			consoleMethod(formattedMessage);
		}

		// Consider integrating con Sentry/LogRocket en producción
		// if (process.env.NODE_ENV === 'production' && level === 'error') {
		//   Sentry.captureException(metadata instanceof Error ? metadata : new Error(message));
		// }
	}

	/**
	 * 🔍 DEBUG - Información detallada para debugging
	 * Solo visible en desarrollo
	 */
	debug(message: string, metadata?: any): void {
		this.log('debug', message, metadata);
	}

	/**
	 * ✅ INFO - Información general sobre el flujo de la aplicación
	 */
	info(message: string, metadata?: any): void {
		this.log('info', message, metadata);
	}

	/**
	 * ⚠️ WARN - Advertencias que no son errores pero requieren atención
	 */
	warn(message: string, metadata?: any): void {
		this.log('warn', message, metadata);
	}

	/**
	 * ❌ ERROR - Errores que afectan la funcionalidad
	 * Visible incluso en producción
	 */
	error(message: string, error?: unknown): void {
		this.log('error', message, error);
	}

	/**
	 * 🎯 Grupo de logs relacionados (útil para operaciones complejas)
	 */
	group(label: string, callback: () => void): void {
		if (!this.shouldLog('debug')) return;

		console.group(`🎯 ${label}`);
		try {
			callback();
		} finally {
			console.groupEnd();
		}
	}

	/**
	 * ⏱️ Medir tiempo de ejecución de una operación
	 */
	time(label: string): void {
		if (!this.shouldLog('debug')) return;
		console.time(`⏱️ ${label}`);
	}

	/**
	 * ⏱️ Terminar medición de tiempo
	 */
	timeEnd(label: string): void {
		if (!this.shouldLog('debug')) return;
		console.timeEnd(`⏱️ ${label}`);
	}
}

// Instancia global del logger
export const logger = new Logger();

// Exportar también como default
export default logger;

/**
 * 📌 GUÍA DE USO:
 *
 * 1. Importar el logger:
 *    import { logger } from '@/shared/utils/logger';
 *
 * 2. Usar niveles apropiados:
 *    - debug: Para debugging detallado (ej: valores de variables, flujo interno)
 *    - info: Para eventos importantes (ej: usuario logueado, formulario enviado)
 *    - warn: Para advertencias (ej: caché expirado, datos inconsistentes)
 *    - error: Para errores (ej: fallo en HTTP, excepción no manejada)
 *
 * 3. Incluir contexto útil:
 *    logger.debug('Casos filtrados', { count: casos.length, filter: selectedYear });
 *    logger.error('Error al cargar usuario', error);
 *
 * 4. Agrupar logs relacionados:
 *    logger.group('Procesando formulario', () => {
 *      logger.debug('Validando campos...');
 *      logger.debug('Enviando al servidor...');
 *      logger.info('Formulario procesado exitosamente');
 *    });
 *
 * 5. Medir performance:
 *    logger.time('Cargar casos');
 *    await fetchCasos();
 *    logger.timeEnd('Cargar casos');
 */
